import { AuthOnFile } from "../../../data-model/medicalPaOrdersModel";

/** Default number of aliases shown before a `+N` leftover suffix. */
export const DEFAULT_SITE_NPI_ALIAS_DISPLAY_LIMIT = 3;

/**
 * Formats site alias values for compact header display.
 *
 * Shows up to `maxVisible` aliases joined by commas, then appends `+N more`
 * when additional aliases remain (e.g. `A, B, C +2 more`).
 *
 * @param aliases - Alias strings from submission test location details.
 * @param maxVisible - Maximum aliases to show before the `+N more` suffix.
 * @returns Formatted label, or `undefined` when no aliases are present.
 */
export const formatSiteNpiAliasesForDisplay = (
  aliases?: string[] | null,
  maxVisible = DEFAULT_SITE_NPI_ALIAS_DISPLAY_LIMIT,
): string | undefined => {
  if (!aliases?.length) {
    return undefined;
  }

  const trimmed = aliases
    .map((alias) => alias?.trim())
    .filter((alias): alias is string => Boolean(alias));

  if (!trimmed.length) {
    return undefined;
  }

  const visible = trimmed.slice(0, maxVisible);
  const leftoverCount = trimmed.length - visible.length;
  const visibleLabel = visible.join(", ");

  if (leftoverCount <= 0) {
    return visibleLabel;
  }

  return `${visibleLabel} +${leftoverCount} more`;
};

/**
 * Resolves a display alias from the first auth-on-file report's test location.
 *
 * @param authOnFileDetails - Optional auth-on-file payload containing reports.
 * @returns Trimmed ``test_location`` from the first report, or a fallback
 *   message when no report / location is available.
 */
export const getAliasFromAuthOnFileReport = (
  authOnFileDetails?: AuthOnFile,
): string => {
  const allReports = authOnFileDetails?.reports ?? [];
  if (allReports.length === 0) {
    return "No test location found";
  }
  const firstReport = allReports[0];
  return firstReport.test_location?.trim()?.length &&
    firstReport.test_location?.trim().length > 0
    ? firstReport.test_location?.trim()
    : "No test location found";
};
